import React from "react";
import Links from "../Links";
import { House, LayoutDashboard, X } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Images from "../Images";
import tw from "tailwind-styled-components";
import PermissionAwareNavItem from "./Permissions/PermissionAwareNavItem";
import { logoutUser } from "../requests/auth/Authentication";
import { useDispatch } from "react-redux";
import { logout } from "../redux/store";

const MobileSidebar = ({ isOpen, onClose }) => {
  const links = Links;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = async () => {
    try {
      await logoutUser();
      dispatch(logout());
      // The logout function in Redux store will handle navigation to landing page
    } catch (error) {
      console.error("Logout failed:", error);
      // If logout fails, still navigate to landing page
      window.location.href = "/";
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/50"
      />

      {/* Sidebar */}
      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: 0 }}
        exit={{ x: "-100%" }}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 200,
        }}
        className="fixed inset-y-0 left-0 z-50 w-[280px] bg-white shadow-xl"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b">
            <img src={Images.logo} alt="Dealboxx - Logo" className="h-8" />
            <button
              onClick={onClose}
              className="p-2 rounded-full transition-colors hover:bg-gray-100"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
          </div>

          {/* Scrollable Navigation */}
          <nav className="overflow-y-auto flex-1">
            <div className="px-4 py-2 h-full">
              <ul className="space-y-2">
                <li>
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      `group flex items-center w-full gap-2 rounded-md p-4 font-semibold text-sm transition duration-300 ease-in-out cursor-pointer ${
                        isActive
                          ? "bg-[#FF6C6C] text-white"
                          : "text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                      }`
                    }
                    onClick={onClose}
                  >
                    <LayoutDashboard className="transition duration-300 group-hover:text-white" />
                    <p className="text-lg font-semibold transition duration-300">
                      Dashboard
                    </p>
                  </NavLink>
                </li>

                <LinkGroup
                  title="Deals Management"
                  links={links.filter((link) => link.type === "Deals")}
                  onClose={onClose}
                  onLogout={handleLogout}
                />
                <LinkGroup
                  title="Order Management"
                  links={links.filter((link) => link.type === "Orders")}
                  onClose={onClose}
                  onLogout={handleLogout}
                />
                <LinkGroup
                title="Branches"
                links={links.filter((link) => link.type === "Branches")}
                handleLogout={handleLogout}
              />
                <LinkGroup
                  title="Vendor Partners"
                  links={links.filter((link) => link.type === "Partners")}
                  onClose={onClose}
                  onLogout={handleLogout}
                />
                <LinkGroup
                  title="Affiliates"
                  links={links.filter((link) => link.type === "Affiliates")}
                  onClose={onClose}
                  handleLogout={handleLogout}
                />

                <LinkGroup
                  title="Campaign Management"
                  links={links.filter((link) => link.type === "Campaigns")}
                  onClose={onClose}
                  onLogout={handleLogout}
                />

                <LinkGroup
                  title="Profile & Settings"
                  links={links.filter((link) => link.type === "Settings")}
                  onClose={onClose}
                  onLogout={handleLogout}
                />

                <LinkGroup
                  title="Help Center"
                  links={links.filter((link) => link.type === "Help")}
                  onClose={onClose}
                  onLogout={handleLogout}
                />
              </ul>
            </div>
          </nav>
        </div>
      </motion.div>
    </>
  );
};

const LinkGroup = ({ title, links, onClose, onLogout }) => {
  // List of features that are coming soon
  const comingSoonFeatures = ["Help & Support"];

  return (
    <div className="flex flex-col">
      <LinkGroupTitle>{title}</LinkGroupTitle>
      {links.map((link, index) => (
        <PermissionAwareNavItem
          key={index}
          link={link}
          isCollapsed={false}
          isMobile={true}
          onClose={onClose}
          comingSoonLinks={comingSoonFeatures}
          onLogout={onLogout}
        />
      ))}
    </div>
  );
};

const LinkGroupTitle = tw.p`text-sm font-bold text-black mt-3 p-3 w-full`;

export default MobileSidebar;
